// Gemensam, säker radering av dokument: papperskorg (soft delete), återställning
// och permanent radering – med kontroller innan något tas bort.
import { supabase } from "@/integrations/supabase/client";

export type DeletableDocumentType =
  | "risk_assessment"
  | "skr_assessment"
  | "work_preparation";

type TableName = "folder_risk_assessments" | "skr_assessments" | "work_preparations";

export const DOCUMENT_TABLES: Record<DeletableDocumentType, TableName> = {
  risk_assessment: "folder_risk_assessments",
  skr_assessment: "skr_assessments",
  work_preparation: "work_preparations",
};

export const DOCUMENT_LABELS: Record<DeletableDocumentType, string> = {
  risk_assessment: "Risksammanställning",
  skr_assessment: "SKR-bedömning",
  work_preparation: "Arbetsberedning",
};

export interface DeletionTarget {
  type: DeletableDocumentType;
  id: string;
  name: string;
  docNumber?: string;
  projectId?: string | null;
}

export interface DeletionWarning {
  kind: "project_locked" | "external_share" | "export" | "dependency";
  message: string;
  /** Hindrar radering helt (inte bara varning) */
  blocking?: boolean;
}

export interface TrashItem {
  id: string;
  type: DeletableDocumentType;
  name: string;
  deleted_at: string;
  delete_reason: string | null;
  deleted_by: string | null;
}

const currentUserId = async (): Promise<string | null> => {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
};

/** Kontrollerar konsekvenser innan ett dokument tas bort. */
export const checkDeletion = async (target: DeletionTarget): Promise<DeletionWarning[]> => {
  const warnings: DeletionWarning[] = [];
  const projectId = target.projectId ?? null;

  if (projectId) {
    // 1. Avslutat/överlämnat projekt
    const [{ data: handovers }, { data: archive }] = await Promise.all([
      supabase
        .from("project_handovers")
        .select("status, handed_over_at")
        .eq("project_id", projectId)
        .not("handed_over_at", "is", null)
        .limit(1),
      supabase
        .from("project_archive_state")
        .select("is_archived")
        .eq("project_id", projectId)
        .maybeSingle(),
    ]);

    if ((handovers && handovers.length > 0) || archive?.is_archived) {
      warnings.push({
        kind: "project_locked",
        blocking: true,
        message:
          "Projektet är överlämnat eller avslutat. Dokumentet ingår i leveransen och kan därför inte tas bort.",
      });
    }

    // 2. Externa delningar
    const { data: shares } = await supabase
      .from("document_shares")
      .select("recipient_email, recipient_name, revoked_at")
      .eq("project_id", projectId)
      .eq("document_id", target.id)
      .is("revoked_at", null);

    if (shares && shares.length > 0) {
      const names = shares
        .map((s) => s.recipient_name || s.recipient_email)
        .filter(Boolean)
        .join(", ");
      warnings.push({
        kind: "external_share",
        message: `Dokumentet är delat externt med ${names}. Länkarna spärras och mottagaren ser "Dokumentet är borttaget av avsändaren".`,
      });
    }

    // 3. Word-exporter / leveranspaket
    const { count: exportCount } = await supabase
      .from("document_exports")
      .select("id", { count: "exact", head: true })
      .eq("project_id", projectId)
      .eq("document_id", target.id);

    if ((exportCount ?? 0) > 0) {
      warnings.push({
        kind: "export",
        message: `Dokumentet har exporterats till Word ${exportCount} gång(er). Exportkvittona sparas kvar som bevis på vad som gällde vid tillfället.`,
      });
    }

    // 4. Beroenden – dokument som matas av det här
    if (target.type === "risk_assessment") {
      const { count: prepCount } = await supabase
        .from("work_preparations")
        .select("id", { count: "exact", head: true })
        .eq("source_risk_assessment_id", target.id)
        .is("deleted_at", null);

      if ((prepCount ?? 0) > 0) {
        warnings.push({
          kind: "dependency",
          message: `${prepCount} arbetsberedning(ar) bygger på den här risksammanställningen och tappar sitt underlag.`,
        });
      }

      warnings.push({
        kind: "dependency",
        message:
          "Arbetsmiljöplanen och sammanställningarna räknas om utan det här dokumentet.",
      });
    }
  }

  return warnings;
};

const logAudit = async (
  target: DeletionTarget,
  action: "deleted" | "restored" | "purged",
  reason?: string,
) => {
  if (!target.projectId) return;
  const userId = await currentUserId();
  let actorName: string | null = null;
  if (userId) {
    const { data } = await supabase
      .from("user_profiles")
      .select("full_name")
      .eq("id", userId)
      .maybeSingle();
    actorName = (data as { full_name?: string } | null)?.full_name ?? null;
  }

  await supabase.from("document_audit_log").insert({
    project_id: target.projectId,
    document_type: target.type,
    doc_number: target.docNumber ?? null,
    table_name: DOCUMENT_TABLES[target.type],
    record_id: target.id,
    action,
    actor_id: userId,
    actor_name: actorName,
    changes: { name: { from: target.name, to: reason ?? null } },
  });
};

/** Spärrar externa delningslänkar till dokumentet. */
const revokeShares = async (target: DeletionTarget) => {
  if (!target.projectId) return;
  await supabase
    .from("document_shares")
    .update({ revoked_at: new Date().toISOString() })
    .eq("project_id", target.projectId)
    .eq("document_id", target.id)
    .is("revoked_at", null);
};

/** Flyttar dokumentet till papperskorgen. */
export const softDeleteDocument = async (
  target: DeletionTarget,
  reason: string,
): Promise<void> => {
  const table = DOCUMENT_TABLES[target.type];
  const userId = await currentUserId();

  const { error } = await supabase
    .from(table)
    .update({
      deleted_at: new Date().toISOString(),
      deleted_by: userId,
      delete_reason: reason || null,
    } as never)
    .eq("id", target.id);

  if (error) throw error;

  await revokeShares(target);
  await logAudit(target, "deleted", reason);
};

/** Återställer ett dokument från papperskorgen. */
export const restoreDocument = async (target: DeletionTarget): Promise<void> => {
  const table = DOCUMENT_TABLES[target.type];
  const { error } = await supabase
    .from(table)
    .update({ deleted_at: null, deleted_by: null, delete_reason: null } as never)
    .eq("id", target.id);

  if (error) throw error;
  await logAudit(target, "restored");
};

/** Raderar dokumentet permanent. */
export const purgeDocument = async (target: DeletionTarget): Promise<void> => {
  const table = DOCUMENT_TABLES[target.type];
  await logAudit(target, "purged");
  const { error } = await supabase.from(table).delete().eq("id", target.id);
  if (error) throw error;
};

/** Hämtar allt som ligger i projektets papperskorg. */
export const fetchTrash = async (projectId: string): Promise<TrashItem[]> => {
  const [risk, skr, prep] = await Promise.all([
    supabase
      .from("folder_risk_assessments")
      .select("id, name, deleted_at, delete_reason, deleted_by")
      .eq("project_id", projectId)
      .not("deleted_at", "is", null),
    supabase
      .from("skr_assessments")
      .select("id, name, deleted_at, delete_reason, deleted_by")
      .eq("project_id", projectId)
      .not("deleted_at", "is", null),
    supabase
      .from("work_preparations")
      .select("id, name, deleted_at, delete_reason, deleted_by")
      .eq("project_id", projectId)
      .not("deleted_at", "is", null),
  ]);

  const map = (rows: unknown[], type: DeletableDocumentType): TrashItem[] =>
    (rows as TrashItem[]).map((r) => ({ ...r, type }));

  return [
    ...map(risk.data ?? [], "risk_assessment"),
    ...map(skr.data ?? [], "skr_assessment"),
    ...map(prep.data ?? [], "work_preparation"),
  ].sort((a, b) => String(b.deleted_at).localeCompare(String(a.deleted_at)));
};
